import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { parseGpxText } from "../lib/gpx";
import PlaylistForm from "../components/PlaylistForm";
import VideoFormStandalone from "../components/VideoForm";
import VideoMarkersEditor from "../components/VideoMarkersEditor";

export default function CreatorStudio() {
  const { user, myCreator, refresh, setMyCreator } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterRoute, setFilterRoute] = useState("__all__");
  const [sortKey, setSortKey] = useState("created_at");

  const [editingGroup, setEditingGroup] = useState(null);
  const [editingGroupRoute, setEditingGroupRoute] = useState(null);
  const [showGroupDialog, setShowGroupDialog] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [videosForGroup, setVideosForGroup] = useState([]);
  const [draggingVideoID, setDraggingVideoID] = useState(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [subsetStartIndex, setSubsetStartIndex] = useState(null);
  const [subsetEndIndex, setSubsetEndIndex] = useState(null);
  const [markerVideo, setMarkerVideo] = useState(null);

  useEffect(() => {
    if (!user) navigate("/");
    if (!myCreator) return; // creator profile required
    loadAll();
  }, [user, myCreator, navigate]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: gs }, { data: rs }] = await Promise.all([
        api.get("/me/video-groups"),
        api.get("/routes", { params: { page: 1, page_size: 200 } }),
      ]);
      setGroups(Array.isArray(gs) ? gs : gs.items || []);
      setRoutes(Array.isArray(rs) ? rs : rs.items || []);
    } catch (e) {
      toast.error("Failed to load creator studio");
    } finally {
      setLoading(false);
    }
  };

  const openCreateGroup = () => {
    setEditingGroup({
      title: "",
      description: "",
      route_id: "",
      youtube_playlist_url: "",
    });
    setShowGroupDialog(true);
  };

  const saveGroup = async (group) => {
    try {
      if (group.id) {
        await api.put(`/me/video-groups/${group.id}`, group);
        toast.success("Playlist updated");
      } else {
        await api.post(`/me/video-groups`, group);
        toast.success("Playlist created");
      }
      setShowGroupDialog(false);
      loadAll();
    } catch (e) {
      toast.error("Failed to save playlist");
    }
  };

  const delGroup = async (group) => {
    if (!globalThis.confirm("Delete this playlist?")) return;
    try {
      await api.delete(`/me/video-groups/${group.id}`);
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Failed to delete playlist");
    }
  };

  const viewGroup = async (g) => {
    setSelectedGroup(g);
    // load route details and filter videos by group
    try {
      const { data } = await api.get(`/routes/${g.route_id}`);
      const vids = (data.videos || [])
        .filter((v) => v.group_id === g.id)
        .slice()
        .sort(
          (a, b) =>
            (Number(a.index) || 0) - (Number(b.index) || 0) ||
            new Date(a.created_at || 0) - new Date(b.created_at || 0),
        );
      setEditingGroupRoute(data.route);
      setVideosForGroup(vids);
    } catch (e) {
      setVideosForGroup([]);
    }
  };

  const openAddVideo = (g) => {
    setEditingVideo({
      route_id: g.route_id,
      group_id: g.id,
      creator: myCreator?.name || "",
      title: "",
      video_url: "",
      thumbnail: "",
      duration: 0,
      index:
        videosForGroup.length > 0
          ? Math.max(...videosForGroup.map((v) => Number(v.index) || 0)) + 1
          : 0,
    });
    setShowVideoDialog(true);
  };

  const openEditVideo = async (video) => {
    setShowVideoDialog(true);
    setEditingVideo(video);
    try {
      const { data } = await api.get(`/me/videos/${video.id}`);
      setEditingVideo(data || video);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load video details");
    }
  };

  const saveVideo = async (video) => {
    try {
      const rawDuration = Number(
        video?.duration ?? video?.length ?? video?.video_length,
      );
      const payload = {
        ...video,
        creator: video?.creator || myCreator?.name || "",
        duration: Number.isFinite(rawDuration) ? Math.max(0, rawDuration) : 0,
        index: Number.isFinite(Number(video?.index))
          ? Math.max(0, Math.floor(Number(video.index)))
          : 0,
      };
      if (video.id) {
        await api.put(`/me/videos/${video.id}`, payload);
        toast.success("Video updated");
      } else {
        await api.post(`/me/videos`, payload);
        toast.success("Video created");
      }
      setShowVideoDialog(false);
      if (selectedGroup) viewGroup(selectedGroup);
    } catch (e) {
      toast.error("Failed to save video");
    }
  };

  const parseLocalGpxFile = async (file) => parseGpxText(await file.text());

  const fetchCreatorVideoMetadata = async (url) => {
    const { data } = await api.post("/me/videos/metadata", { url });
    return data;
  };

  const persistVideoOrder = async (orderedVideos) => {
    if (!selectedGroup || orderedVideos.length === 0) return;
    const updates = orderedVideos.map((video, idx) => {
      const payload = {
        ...video,
        group_id: selectedGroup.id,
        playlist_id: selectedGroup.id,
        index: idx,
      };
      return api.put(`/me/videos/${video.id}`, payload);
    });
    await Promise.all(updates);
  };

  const handleDropVideo = async (targetVideoID) => {
    if (!draggingVideoID || draggingVideoID === targetVideoID) return;
    const fromIndex = videosForGroup.findIndex((v) => v.id === draggingVideoID);
    const toIndex = videosForGroup.findIndex((v) => v.id === targetVideoID);
    if (fromIndex < 0 || toIndex < 0) return;

    const reordered = [...videosForGroup];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const normalized = reordered.map((video, idx) => ({
      ...video,
      index: idx,
    }));

    setVideosForGroup(normalized);
    setDraggingVideoID(null);
    try {
      await persistVideoOrder(normalized);
      toast.success("Video order updated");
      if (selectedGroup) viewGroup(selectedGroup);
    } catch {
      toast.error("Failed to update video order");
      if (selectedGroup) viewGroup(selectedGroup);
    }
  };

  const normalizeGpxPointsInput = (raw) => {
    const toPoints = (value) => {
      if (!Array.isArray(value)) return [];
      return value
        .filter((p) => Array.isArray(p) && p.length >= 2)
        .map((p) => p.map((v) => Number(v) || 0));
    };

    if (Array.isArray(raw)) return toPoints(raw);
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return toPoints(parsed);
        if (parsed && typeof parsed === "object") {
          return toPoints(
            parsed.gpx_points || parsed.gpxPoints || parsed.points || [],
          );
        }
      } catch {
        return [];
      }
      return [];
    }
    if (raw && typeof raw === "object") {
      return toPoints(raw.gpx_points || raw.gpxPoints || raw.points || []);
    }
    return [];
  };

  const filtered = groups
    ? groups
        .filter((g) =>
          filterRoute && filterRoute !== "__all__"
            ? g.route_id === filterRoute
            : true,
        )
        .sort((a, b) => {
          if (sortKey === "title") return a.title.localeCompare(b.title);
          if (sortKey === "video_count") return b.video_count - a.video_count;
          return new Date(b.created_at) - new Date(a.created_at);
        })
    : [];

  const videoOwnGpxPoints = normalizeGpxPointsInput(
    editingVideo?.gpx_points || [],
  );
  const hasVideoOwnGpx = videoOwnGpxPoints.length > 0;
  const selectedRouteForVideo = routes.find(
    (r) => r.id === editingVideo?.route_id,
  );
  const selectedPlaylistForVideo =
    groups.find((p) => p.id === editingVideo?.group_id) || selectedGroup;
  const sequencedPlaylistPointsForVideo = (
    selectedPlaylistForVideo?.gpx_files || []
  )
    .slice()
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    .flatMap((f) => f.gpx_points || f.gpxPoints || []);

  const playlistMapPointsForVideo =
    sequencedPlaylistPointsForVideo.length > 0
      ? sequencedPlaylistPointsForVideo
      : selectedPlaylistForVideo?.gpx_points ||
        selectedPlaylistForVideo?.gpxPoints ||
        [];
  const routeMapPointsForVideo =
    selectedRouteForVideo?.gpx_points || editingGroupRoute?.gpx_points || [];
  const fallbackMapPointsForVideo =
    Array.isArray(playlistMapPointsForVideo) &&
    playlistMapPointsForVideo.length > 1
      ? playlistMapPointsForVideo
      : routeMapPointsForVideo;
  const videoMapPoints = hasVideoOwnGpx
    ? videoOwnGpxPoints
    : fallbackMapPointsForVideo;
  const selectedSubsetSegment = useMemo(() => {
    if (
      subsetStartIndex == null ||
      subsetEndIndex == null ||
      !Array.isArray(fallbackMapPointsForVideo) ||
      fallbackMapPointsForVideo.length === 0
    ) {
      return null;
    }

    const start = fallbackMapPointsForVideo[subsetStartIndex];
    const end = fallbackMapPointsForVideo[subsetEndIndex];
    if (!Array.isArray(start) || !Array.isArray(end)) return null;

    return {
      start: [Number(start[0]) || 0, Number(start[1]) || 0],
      end: [Number(end[0]) || 0, Number(end[1]) || 0],
    };
  }, [fallbackMapPointsForVideo, subsetStartIndex, subsetEndIndex]);

  const delVideo = async (id) => {
    if (!globalThis.confirm("Delete this video?")) return;
    await api.delete(`/admin/videos/${id}`);
    toast.success("Deleted");
    loadAll();
  };
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Creator Studio</h1>
        <div className="flex items-center gap-3">
          <Select onValueChange={(v) => setSortKey(v)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Newest</SelectItem>
              <SelectItem value="title">Title</SelectItem>
              <SelectItem value="video_count">Video count</SelectItem>
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => setFilterRoute(v)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Filter by route" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All routes</SelectItem>
              {routes.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={openCreateGroup}
            className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white"
          >
            New Playlist
          </Button>
        </div>
      </div>

      <div className="mt-6 grid gap-4">
        {loading ? (
          <div>Loading…</div>
        ) : (
          filtered.map((g) => (
            <div
              key={g.id}
              className="p-4 bg-white border rounded-lg flex items-center justify-between"
            >
              <div>
                <div className="font-bold">{g.title}</div>
                <div className="text-sm text-[#5C665F]">{g.description}</div>
                <div className="text-xs text-[#8A867C] mt-1 flex items-center gap-3">
                  <div>
                    Route:{" "}
                    {routes.find((r) => r.id === g.route_id)?.name ||
                      g.route_id}
                  </div>
                  <div>— {g.video_count || 0} videos</div>
                  <div className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/5">
                    {g.status === "ready" ? "Published" : "Unpublished"}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const route = await api.get(`/routes/${g.route_id}`);
                      if (!route?.data) {
                        toast.error("Failed to load route details");
                        return;
                      }
                      //console.log("route", route.data.route);
                      setEditingGroupRoute(route.data.route);
                    } catch (e) {
                      toast.error("Failed to load route details");
                      return;
                    }
                    setEditingGroup(g);
                    setShowGroupDialog(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(`/routes/${g.route_id}?playlist=${g.id}`)
                  }
                >
                  View
                </Button>
                <Button variant="outline" onClick={() => viewGroup(g)}>
                  Videos
                </Button>
                {g.status === "ready" ? (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await api.put(`/me/video-groups/${g.id}`, {
                          status: "created",
                        });
                        toast.success("Unpublished");
                        loadAll();
                      } catch (e) {
                        toast.error("Failed to unpublish");
                      }
                    }}
                  >
                    Unpublish
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await api.put(`/me/video-groups/${g.id}`, {
                          status: "ready",
                        });
                        toast.success("Published");
                        loadAll();
                      } catch (e) {
                        toast.error("Failed to publish");
                      }
                    }}
                  >
                    Publish
                  </Button>
                )}
                <Button variant="destructive" onClick={() => delGroup(g)}>
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedGroup && (
        <div className="mt-8">
          <h2 className="text-xl font-bold">Videos in {selectedGroup.title}</h2>
          <h3 className="text-l font-bold text-right">Drag to reorder</h3>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => openAddVideo(selectedGroup)}>
              Add Video
            </Button>
            <Button variant="outline" onClick={() => setSelectedGroup(null)}>
              Close
            </Button>
          </div>
          <div className="mt-4 space-y-3">
            {videosForGroup.map((v) => (
              <div
                key={v.id}
                className={`p-3 bg-white border rounded-lg flex items-center justify-between ${draggingVideoID === v.id ? "opacity-60" : ""}`}
                draggable
                onDragStart={() => setDraggingVideoID(v.id)}
                onDragEnd={() => setDraggingVideoID(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDropVideo(v.id);
                }}
              >
                <div>
                  <div className="font-bold">{v.title}</div>
                  <div className="text-xs text-[#5C665F]">
                    #{Number(v.index) || 0} • {v.duration}s • {v.creator}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => openEditVideo(v)}>
                    Edit
                  </Button>
                  <Button variant="outline" onClick={() => setMarkerVideo(v)}>
                    Markers
                  </Button>
                  <Button variant="destructive" onClick={() => delVideo(v.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
            {videosForGroup.length === 0 && (
              <div className="text-sm text-[#8A867C]">No videos yet.</div>
            )}
            {videosForGroup.length > 1 && (
              <div className="text-xs text-[#5C665F]">
                Drag videos to reorder. Index values update automatically.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Group dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGroup?.id ? "Edit Playlist" : "New Playlist"}
            </DialogTitle>
          </DialogHeader>
          {editingGroup && (
            <PlaylistForm
              value={editingGroup}
              onChange={setEditingGroup}
              routes={routes}
              routeContextPoints={editingGroupRoute?.gpx_points || []}
              routeDisabled={!!editingGroup?.id}
              youtubePlaylistUrlDisabled={!!editingGroup?.id}
              gpxUploadMode="single"
              parseUploadedGpx={parseLocalGpxFile}
              fetchPlaylistMetadata={fetchCreatorVideoMetadata}
              allowRouteSubsetCopy
            />
          )}
          <DialogFooter>
            <Button onClick={() => saveGroup(editingGroup)}>Save</Button>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingVideo?.id ? "Edit Video" : "Add Video"}
            </DialogTitle>
          </DialogHeader>
          {editingVideo && (
            <VideoFormStandalone
              value={editingVideo}
              onChange={setEditingVideo}
              routes={[editingGroupRoute]}
              playlists={[selectedGroup]}
              showRouteField={false}
              showPlaylistField={false}
              showStatusField={false}
              showCreatorField={false}
              showDurationField={false}
              parseUploadedGpx={parseLocalGpxFile}
              fetchVideoMetadata={fetchCreatorVideoMetadata}
            />
          )}
          <DialogFooter>
            <Button onClick={() => saveVideo(editingVideo)}>Save</Button>
            <Button variant="outline" onClick={() => setShowVideoDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <VideoMarkersEditor
        video={markerVideo}
        routes={routes}
        editingRoute={editingGroupRoute}
        editingPlaylist={selectedGroup}
        onClose={() => setMarkerVideo(null)}
        onSaved={() => {
          if (selectedGroup) viewGroup(selectedGroup);
          setMarkerVideo(null);
        }}
      />
    </div>
  );
}
